import { useState, useContext, useEffect, useRef } from 'react';
import { Button, Modal, ModalCloseButton, ModalProps, NotificationDispatch, ModalFooter, SwitchThemeContext, ModalHeader, ModalTitle, TextField } from '@tryhackme/thm-ui-components';
import { faUserPlus, faCopy } from '@fortawesome/pro-regular-svg-icons';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { StyledText } from 'src/features/management-dashboard/ctf-builder/components/share-ctf-modal/share-ctf-modal.styles';
import { ThemeColorMode } from 'src/common/enums';
import { useForm } from 'src/common/form';
import { useInviteModuleTeammatesMutation } from 'src/features/module/module.slice';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { useGetUserQuery } from 'src/common/slices';
import { StyledUserPlusIcon, StyledFooterButtonsWrapper, StyledPlaceholder, StyledCopyText, StyledCopyButton, StyledButtonIcon, StyledTextFieldWrapper, StyledCopyTextWrapper } from './invite-teammates.styles';
const copyText = 'Copy link';
const copiedText = 'Copied!';
export const formValidation = yup.object({
  emails: yup.string().required('Field is required.')
});
export const InviteTeammates = ({
  onOpenChange,
  moduleName,
  ...props
}: Omit<ModalProps, 'children'> & {
  moduleName: string;
}) => {
  const [copyButtonText, setCopyButtonText] = useState(copyText);
  const timeOut = useRef<ReturnType<typeof setTimeout>>();
  const moduleUrl = window.location.href;
  const {
    theme
  } = useContext(SwitchThemeContext);
  const isDarkMode = theme === ThemeColorMode.DARK;
  const {
    register,
    reset,
    handleSubmit,
    formState: {
      errors
    }
  } = useForm<{
    emails: string;
  }>({
    defaultValues: {
      emails: ''
    },
    resolver: yupResolver(formValidation)
  });
  const [inviteModuleTeammates, {
    isLoading
  }] = useInviteModuleTeammatesMutation();
  const {
    data: userData
  } = useGetUserQuery();
  useEffect(() => () => {
    clearTimeout(timeOut.current);
  }, []);
  const handleOnCopy = async () => {
    await navigator.clipboard.writeText(moduleUrl);
    setCopyButtonText(copiedText);
    timeOut.current = setTimeout(() => {
      setCopyButtonText(copyText);
    }, 2000);
  };
  const handleInviteTeammates = async (data: {
    emails: string;
  }) => {
    try {
      await inviteModuleTeammates({
        emails: data.emails,
        moduleUrl,
        moduleName,
        invitee: userData?.data?.user.email ?? ''
      }).unwrap();
      NotificationDispatch('success', 'Teammates were invited successfully.');
      onOpenChange?.(false);
      reset();
    } catch (error) {
      if (isQueryErrorWithMessage(error)) {
        NotificationDispatch('error', error.data.message);
      } else {
        NotificationDispatch('error', 'An unknown error has occurred.');
      }
    }
  };
  if (!userData) return null;
  return <Modal onOpenChange={onOpenChange} {...props} data-sentry-element="Modal" data-sentry-component="InviteTeammates" data-sentry-source-file="invite-teammates.tsx">
      <ModalHeader data-sentry-element="ModalHeader" data-sentry-source-file="invite-teammates.tsx">
        <StyledUserPlusIcon icon={faUserPlus} data-sentry-element="StyledUserPlusIcon" data-sentry-source-file="invite-teammates.tsx" />
        <ModalTitle data-sentry-element="ModalTitle" data-sentry-source-file="invite-teammates.tsx">Invite teammates</ModalTitle>
      </ModalHeader>
      <main>
        <StyledText data-sentry-element="StyledText" data-sentry-source-file="invite-teammates.tsx">
          Invite your teammates to this CTF event by sharing the link below or entering their emails.
        </StyledText>
        <p>Event link</p>
        <StyledCopyTextWrapper data-sentry-element="StyledCopyTextWrapper" data-sentry-source-file="invite-teammates.tsx">
          <StyledPlaceholder $isDarkMode={isDarkMode} data-sentry-element="StyledPlaceholder" data-sentry-source-file="invite-teammates.tsx">
            <StyledCopyText data-sentry-element="StyledCopyText" data-sentry-source-file="invite-teammates.tsx">{moduleUrl}</StyledCopyText>
          </StyledPlaceholder>
          {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
          <StyledCopyButton onClick={() => handleOnCopy()} $isDarkMode={isDarkMode} data-sentry-element="StyledCopyButton" data-sentry-source-file="invite-teammates.tsx">
            <StyledButtonIcon icon={faCopy} data-sentry-element="StyledButtonIcon" data-sentry-source-file="invite-teammates.tsx" /> {copyButtonText}
          </StyledCopyButton>
        </StyledCopyTextWrapper>
        <p>or enter their emails below</p>
        <StyledTextFieldWrapper data-sentry-element="StyledTextFieldWrapper" data-sentry-source-file="invite-teammates.tsx">
          <TextField variant="textarea" rows={4} {...register('emails')} isError={!!errors.emails?.message} errorMessage={errors.emails?.message} data-sentry-element="TextField" data-sentry-source-file="invite-teammates.tsx" />
        </StyledTextFieldWrapper>
      </main>
      <ModalFooter isSmall data-sentry-element="ModalFooter" data-sentry-source-file="invite-teammates.tsx">
        <StyledFooterButtonsWrapper data-sentry-element="StyledFooterButtonsWrapper" data-sentry-source-file="invite-teammates.tsx">
          <ModalCloseButton text="Cancel" data-sentry-element="ModalCloseButton" data-sentry-source-file="invite-teammates.tsx" />
          <Button variant="solid" color="primary"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onClick={handleSubmit(handleInviteTeammates)} isLoading={isLoading} type="button" data-sentry-element="Button" data-sentry-source-file="invite-teammates.tsx">
            Invite teammates
          </Button>
        </StyledFooterButtonsWrapper>
      </ModalFooter>
    </Modal>;
};